import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  getAuth, 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  updateProfile,
  sendEmailVerification,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { checkInvitedUser, markInvitedUserAsUsed } from '../services/invitedUserService';

// Define the User interface
export interface User extends FirebaseUser {
   // Add any additional user properties here
}

// Define the context interface
interface AuthContextType {
   currentUser: User | null;
   loading: boolean;
   signIn: (email: string, password: string) => Promise<{ isInvited: boolean }>;
   signUp: (
      email: string,
      password: string,
      displayName: string
   ) => Promise<{ isInvited: boolean }>;
   signOut: () => Promise<void>;
   updateUserProfile: (displayName: string, photoURL?: string) => Promise<void>;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props interface
interface AuthProviderProps {
   children: ReactNode;
}

// Create the provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
   const [currentUser, setCurrentUser] = useState<User | null>(null);
   const [loading, setLoading] = useState<boolean>(true);
   const auth = getAuth();

   // Sign in with email and password
   const signIn = async (email: string, password: string): Promise<{ isInvited: boolean }> => {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check if user has active invitation OR was originally invited
      let isInvited = false;
      try {
         // First check for active invitations
         const hasActiveInvitation = await checkInvitedUser(email);

         // Then check if user was originally invited (permanent flag)
         let wasOriginallyInvited = false;
         if (userCredential.user) {
            const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
            if (userDoc.exists()) {
               wasOriginallyInvited = userDoc.data()?.invited || false;
            }
         }

         // User is considered "invited" if they have active invitation OR were originally invited
         isInvited = hasActiveInvitation || wasOriginallyInvited;

         // CRITICAL FIX: If user is determined to be invited but doesn't have permanent flag,
         // update their user document to ensure consistency across all contexts
         if (isInvited && !wasOriginallyInvited && userCredential.user) {
            try {
               const userDocRef = doc(db, 'users', userCredential.user.uid);
               await updateDoc(userDocRef, {
                  invited: true,
                  invitedUpdatedAt: serverTimestamp(),
               });
               console.log('✅ Updated user document with permanent invited flag:', userCredential.user.uid);
            } catch (updateError) {
               console.error('❌ Failed to update user document with invited flag:', updateError);
               // Don't fail login if we can't update the flag, but log the issue
            }
         }

         console.log('🔍 Login invitation check:', {
            email,
            hasActiveInvitation,
            wasOriginallyInvited,
            finalIsInvited: isInvited,
            note: 'Users with active invitations OR who were originally invited get subscription bypass',
         });
      } catch (error) {
         console.error('Error checking invited user during login:', error);
      }

      return { isInvited };
   };

   // Sign up with email and password
   const signUp = async (
      email: string,
      password: string,
      displayName: string
   ): Promise<{ isInvited: boolean }> => {
      // Check if user was invited before creating account
      let isInvited = false;
      try {
         isInvited = await checkInvitedUser(email);
      } catch (error) {
         console.error('Error checking invited user during signup:', error);
      }

      try {
         const userCredential = await createUserWithEmailAndPassword(auth, email, password);

         // Update the user profile with display name
         if (userCredential.user) {
            await updateProfile(userCredential.user, {
               displayName,
            });

            // Create user document in Firestore
            try {
               await setDoc(doc(db, 'users', userCredential.user.uid), {
                  email: userCredential.user.email,
                  displayName,
                  photoURL: userCredential.user.photoURL,
                  createdAt: new Date().toISOString(),
                  invited: isInvited, // Set invited flag based on invitation status
               });
               console.log(
                  `✅ AuthContext: Created user document for ${userCredential.user.uid} with invited: ${isInvited}`
               );
            } catch (userDocError) {
               console.error(
                  `❌ AuthContext: Failed to create user document for ${userCredential.user.uid}:`,
                  userDocError
               );
               throw new Error('Failed to create user profile in AuthContext');
            }

            // Send Firebase verification email (does not rely on server SendGrid)
            try {
               await sendEmailVerification(userCredential.user, {
                  url: `${window.location.origin}/login`,
                  handleCodeInApp: false,
               });
            } catch (verifyErr) {
               console.warn('AuthContext: sendEmailVerification after signup failed:', verifyErr);
            }

            // If user was invited, clean up the temporary invitation after successful signup (non-blocking)
            if (isInvited) {
               fetch(`${process.env.REACT_APP_API_URL}/api/invited-users/cleanup`, {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ email }),
               })
               .then(response => {
                  if (response.ok) {
                     console.log('✅ Cleaned up temporary invitation after signup:', email);
                  } else {
                     console.warn('Failed to cleanup temporary invitation after signup');
                  }
               })
               .catch(error => console.error('Error cleaning up temporary invitation after signup:', error));
            }
         }

         return { isInvited };
      } catch (error) {
         console.error('Error signing up:', error);
         throw error;
      }
   };

   // Sign out
   const signOut = async (): Promise<void> => {
      await firebaseSignOut(auth);
   };

   // Update user profile
   const updateUserProfile = async (displayName: string, photoURL?: string): Promise<void> => {
      try {
         if (!auth.currentUser) {
            throw new Error('No user is signed in');
         }

         const updateData: { displayName: string; photoURL?: string } = {
            displayName,
         };

         if (photoURL) {
            updateData.photoURL = photoURL;
         }

         await updateProfile(auth.currentUser, updateData);

         // Update user document in Firestore
         await updateDoc(doc(db, 'users', auth.currentUser.uid), {
            displayName,
            ...(photoURL && { photoURL }),
            updatedAt: new Date().toISOString(),
         });

         // Refresh the current user
         setCurrentUser({ ...auth.currentUser } as User);
      } catch (error) {
         console.error('Error updating profile:', error);
         throw error;
      }
   };

   // Listen for auth state changes
   useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
         setCurrentUser(user as User);
         setLoading(false);
      });

      return unsubscribe;
   }, [auth]);

   const value = {
      currentUser,
      loading,
      signIn,
      signUp,
      signOut,
      updateUserProfile,
   };

   return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

// Custom hook for using the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 